import { SensorStatus } from "@sitescene/types";
import { X } from "@untitledui/icons";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { SensorsStatusModal } from "@/features/sensors/components/modals/sensors-status-modal";
import { useSensorSummary } from "@/features/sensors/hooks/useSensorSummary";

const BANNER_CSS_VAR = "--sensor-banner-height";

/**
 * Full-width banner that appears when one or more sensors are offline.
 *
 * Sets --sensor-banner-height on <html> so the fixed sidebar and the outlet
 * wrapper in ProtectedLayout can automatically offset without any banner
 * code living in layout files.
 */
interface SensorsOfflineBannerProps {
	/**
	 * When true, renders a dismiss button and activates the un-dismiss logic
	 * (re-surfacing the banner if the offline count strictly increases past
	 * the value at dismiss time).
	 *
	 * Intentionally parked at the default `false` — the single callsite
	 * (`ProtectedLayout` in `auth-guard.tsx`) treats offline sensors as a
	 * sensor-health signal that operators should always see. Kept as a prop
	 * so a future per-tenant / per-user override can opt in without
	 * re-deriving the dismiss UX.
	 */
	isDismissible?: boolean;
}
export const SensorsOfflineBanner = ({
	isDismissible = false,
}: SensorsOfflineBannerProps) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const [isSensorStatusModalOpen, setIsSensorStatusModalOpen] = useState(false);
	const [selectedModalStatus, setSelectedModalStatus] =
		useState<null | SensorStatus>(SensorStatus.Offline);
	const { isSensorSummaryError, isSensorSummarySuccess, sensorSummaryData } =
		useSensorSummary({
			refetchInterval: 20000,
		}); // 20 seconds
	const bannerRef = useRef<HTMLDivElement>(null);

	const offlineCount = sensorSummaryData?.offline ?? 0;
	const isVisible =
		!isDismissed &&
		offlineCount > 0 &&
		isSensorSummarySuccess &&
		!isSensorSummaryError;

	// If the user dismissed the banner and the offline count later grows, the
	// situation has changed enough to re-surface the banner. Track the last
	// seen count in a ref so we only un-dismiss on strict increases. This is
	// intentionally sticky: if a user dismisses at count=3, the count drops
	// to 2, then returns to 3, the banner stays hidden — the condition is
	// "more broken than when you dismissed", not "anything non-zero again".
	// Deferred via queueMicrotask to satisfy react-hooks/set-state-in-effect.
	const lastOfflineCountRef = useRef(offlineCount);
	useEffect(() => {
		const shouldResetDismissed =
			offlineCount > lastOfflineCountRef.current && isDismissed;
		lastOfflineCountRef.current = offlineCount;
		if (shouldResetDismissed) {
			queueMicrotask(() => setIsDismissed(false));
		}
	}, [offlineCount, isDismissed]);

	// Keep --sensor-banner-height in sync so sidebar + content can offset
	// themselves. Re-measure on resize because the label may word-wrap at
	// narrow widths, which changes offsetHeight without re-rendering.
	useLayoutEffect(() => {
		const el = bannerRef.current;
		const root = document.documentElement;

		if (!isVisible || !el) {
			root.style.setProperty(BANNER_CSS_VAR, "0px");
			return;
		}

		const applyHeight = () => {
			root.style.setProperty(BANNER_CSS_VAR, `${el.offsetHeight}px`);
		};
		applyHeight();

		const observer = new ResizeObserver(applyHeight);
		observer.observe(el);

		return () => {
			observer.disconnect();
			root.style.setProperty(BANNER_CSS_VAR, "0px");
		};
	}, [isVisible]);

	const label = `Important: ${offlineCount} Sensor${offlineCount === 1 ? "" : "s"} Offline`;

	return (
		<>
			{isVisible && (
				<div
					className="fixed inset-x-0 top-0 z-50 flex w-full items-center justify-center bg-utility-severity-high-400"
					ref={bannerRef}
				>
					<button
						className="group flex w-full cursor-pointer items-center justify-center gap-1.5 px-4 py-2.5 text-center text-sm font-semibold text-white"
						onClick={() => {
							setSelectedModalStatus(SensorStatus.Offline);
							setIsSensorStatusModalOpen(true);
						}}
						type="button"
					>
						{label}
					</button>

					{isDismissible && (
						<button
							aria-label="Dismiss offline sensors banner"
							className="absolute top-1/2 right-3 -translate-y-1/2 rounded p-1 text-white opacity-70 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
							onClick={() => setIsDismissed(true)}
							type="button"
						>
							<X aria-hidden className="size-4" />
						</button>
					)}
				</div>
			)}
			{/* Render the modal unconditionally so its open/close lifecycle is */}
			{/* driven only by the user. If the offline count drops to 0 (or the */}
			{/* user dismisses the banner) while the modal is open, the modal */}
			{/* stays open instead of unmounting underneath the user. */}
			<SensorsStatusModal
				defaultFilter={selectedModalStatus}
				isOpen={isSensorStatusModalOpen}
				offlineCount={offlineCount}
				onClose={() => setIsSensorStatusModalOpen(false)}
				onSelectedStatusChange={setSelectedModalStatus}
				selectedStatus={selectedModalStatus}
				source="offline_banner"
			/>
		</>
	);
};
